import './TableWidget.scss';
import { useAuthState } from '../../services/auth';
import { Button, ButtonGroup, Modal, OverlayTrigger, Table, Tooltip, Form } from 'react-bootstrap';
import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import Pagination from 'rc-pagination';
import 'rc-pagination/assets/index.css';
import { debounce, isEmpty } from 'lodash';
import axios from 'axios';
import { StringUtils } from '../../util/string-utils';
import { BreakdownList } from '../breakdown-list/BreakdownList';
import { ToastMessage } from '../toast/ToastMessage';
import { ConfirmModal } from '../confirm-modal/ConfirmModal';
import { ArrowClockwise, X } from 'react-bootstrap-icons';
import { useParams } from 'react-router-dom';
import { RegisterAccount } from '../register-account/RegisterAccount';
import { useToast } from '../../services/ToastContext';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '../loading-spinner/LoadingSpinner';

type Props = {
  title?: any;
  subTitle?: any;
  url: any;
  countPerPage: any;
  defaultDate?: string;
  isCve?: boolean;
  isDns?: boolean;
  hideDates?: boolean;
  params?: any;
  hasSearch?: boolean;
  showAddAccount?: boolean;
  dates?: string[];
  interests?: any;
  onRowSelect?: any;
  onDelete?: any;
  dnsRecords?: any;
  icon?: string;
  onClose?: any;
  searchPositionRight?: boolean;
  hideTimes?: boolean;
  startDate?: string;
  endDate?: string;
  deviceId?: any;
  cpeId?: any;
  selectedDate?: string;
  hasCategoryFilter?: boolean;
  height?: string;
  data?: any[];
  type?: string;
  urlSearch?: string;
  needCategories?: boolean;
  selectedKey?: string;
  countSearch?: string;
  q?: string;
  category?: string;
  riskScore?: any;
  unverifiedByDate?: any;
  firmwareVersion?: any;
  unverified?: any;
  deviceType?: any;
  dnsStatus?: any;
};

export const TableWidget = (props: Props) => {
  const { t } = useTranslation();
  const { token } = useAuthState();
  const searchRef: any = useRef(null);
  const confirmDeleteRef: any = useRef(null);
  const interestInputRef: any = useRef(null);
  const { operatorId } = useParams();
  const [allData, setAllData] = useState<any>();
  const registerAccountModal: any = useRef(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [total, setTotal] = useState<number>();
  const [currentPage, setCurrentPage] = useState(1);
  const [collection, setCollection] = useState<any>();
  const [tableHead, setTableHead] = useState<any>();
  const [modalDetails, setModalDetails] = useState<any>();
  const [selectedDate, setSelectedDate] = useState<any>(0);
  const [selectedDateString, setSelectedDateString] = useState<any>(props.defaultDate || props.selectedDate || '');
  const [startDate, setStartDate] = useState<string>(props.startDate || '');
  const [endDate, setEndDate] = useState<string>(props.endDate || '');
  const [deviceId, setDeviceId] = useState<any[]>(props.deviceId || []);
  const [cpeId, setCpeId] = useState<any[]>(props.cpeId || []);
  const [type, settype] = useState<string>(props.type || '');
  const [selectedKey, setSelectedKey] = useState<any>(props.selectedKey || '');
  const [countSearch, setCountSearch] = useState<any>(props.countSearch || '');
  const [selectedRow, setSelectedRow] = useState<any>();
  const [tags, setTags] = useState<string[]>([]);
  const [showAccount, setShowAccount] = useState(false);
  const toggleAccount = () => {
    setShowAccount(!showAccount);
  };
  const [loading, setLoading] = useState<boolean>(true);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const { showToastMessage } = useToast();
  const [categorySearch, setCategories] = useState<any>();
  const [categoryFilter, setCategoriesFilter] = useState('');
  const [search, setSearch] = useState<string>('');
  const [hasQuery, setHasQuery] = useState(false);

  useEffect(() => {
    if (props.interests) {
      setTags(props.interests.split(' '));
    }
    init();
  }, [token]);

async function init() {
  setCategories([]);
  setLoading(true);
  let url = `/${props.url}?page=1&size=${props.countPerPage}&l=${selectedDateString}&startDate=${startDate}&endDate=${endDate}&type=${type}&countSearch=${countSearch}&url=${
    props.urlSearch ? props.urlSearch : ''
  }&selectedKey=${selectedKey}`;

  // Add new parameters if they exist in props
  if (props.firmwareVersion !== undefined) {
    url += `&firmwareVersion=${props.firmwareVersion}`;
  }
  if (props.unverifiedByDate !== undefined) {
    url += `&unverifiedByDate=${props.unverifiedByDate}`;
  }
  if (props.riskScore !== undefined) {
    url += `&riskScore=${props.riskScore}`;
  }
  if (props.unverified !== undefined) {
    url += `&unverified=${props.unverified}`;
  }
  if (props.deviceType !== undefined) {
    url += `&deviceType=${props.deviceType}`;
  }
  if (props.dnsStatus !== undefined) {
    url += `&dnsStatus=${props.dnsStatus}`;
  }
  if (props.isDns) {
    await axios
      .post(url, { 
        params: props.params, 
        cpeId: props.cpeId ? props.cpeId : [], 
        devId: props.deviceId ? props.deviceId : [], 
        category: props.category ? props.category : "" 
      })
      .then((resp: any) => {
        setAllData(resp.data.data);
        setCollection(resp.data.data);
        setTotal(resp.data.records);
        setTableHead(resp.data.headers?.headers || resp.data.headers);
        setLoading(false);
        if (props.needCategories) {
          setCategories(resp.data.categories);
          setCategoriesFilter('');
        }
      });
  } else {
    axios.get(url, { params: props.params }).then((resp: any) => {
      setAllData(resp.data.data);
      setCollection(resp.data.data);
      setTotal(resp.data.records);
      setTableHead(resp.data.headers?.headers || resp.data.headers);
      setLoading(false);
    });
  }
}

  async function addAccount(event: { preventDefault: () => void }) {
    event.preventDefault(); // prevent the default form submission action

    const data = await axios
      .post('/op/auth/register', {
        email: email,
        pass: password,
        confPass: confirmPassword,
      })
      .then((response) => {
        showToastMessage(t('tableWidget.toast.accountRegistered'));
        if (props.onClose) props.onClose();
        toggleAccount();
      })
      .catch((error) => {
        console.error('Error:', error.message);
      });
    setLoading(false);
  }

const searchRecords = async (e: any) => {
  setAllData([]);
  setCollection([]);
  setLoading(true);
  e.preventDefault();
  const val = e.target.value;

  if (!val || isEmpty(val)) {
    setSearch('')
    setCurrentPage(1);
    init();
    setTotal(allData ? allData.length : 0);
  } else {
    let url = `/${props.url}?page=1&size=${props.countPerPage}&q=${
    val || ''
  }&l=${selectedDateString}&startDate=${startDate}&endDate=${endDate}&type=${type}&countSearch=${countSearch}&url=${
    props.urlSearch ? props.urlSearch : ''
  }&selectedKey=${selectedKey}`;

  // Add new parameters if they exist in props
  if (props.firmwareVersion !== undefined) {
    url += `&firmwareVersion=${props.firmwareVersion}`;
  }
    if (props.unverifiedByDate !== undefined) {
    url += `&unverifiedByDate=${props.unverifiedByDate}`;
  }
  if (props.riskScore !== undefined) {
    url += `&riskScore=${props.riskScore}`;
  }
  if (props.unverified !== undefined) {
    url += `&unverified=${props.unverified}`;
  }
  if (props.deviceType !== undefined) {
    url += `&deviceType=${props.deviceType}`;
  }
  if (props.dnsStatus !== undefined) {
    url += `&dnsStatus=${props.dnsStatus}`;
  }
    if (props.isDns) {
      const resp = await axios.post(
        url,
        { params: props.params, cpeId: props.cpeId ? props.cpeId : [], devId: props.deviceId ? props.deviceId : [] }
      );
      setCurrentPage(1);
      setCollection(resp.data.data);
      setTotal(resp.data.records);
      setSearch(val)
      if (props.needCategories) {
        setCategories(resp.data.categories);
      }
      setLoading(false);
    } else {
      const resp = await axios.get(
        url,
        props.params
      );
      setCurrentPage(1);
      const data = resp.data.data;
      setCollection(data);
      setTotal(resp.data.records);
      setLoading(false);
    }
  }
};

  const debouncedResults = useMemo(() => {
    return debounce(searchRecords, 500);
  }, [selectedDateString]);

const updatePage = async (currentPage: any, val?: string, category?: string) => {
  setLoading(true);
  if (!val) {
    val = searchRef.current?.value;
  }

  if (Array.isArray(val) && val?.length) {
    val = tags.join('|');
  }

  setCurrentPage(currentPage);
  
  let url = `/${props.url}?page=${currentPage}&size=${props.countPerPage}&l=${selectedDateString}&startDate=${startDate}&endDate=${endDate}&type=${type}&countSearch=${countSearch}&url=${
    props.urlSearch ? props.urlSearch : ''
  }&selectedKey=${selectedKey}`;
  
  // Add new parameters if they exist in props
  if (props.firmwareVersion !== undefined) {
    url += `&firmwareVersion=${props.firmwareVersion}`;
  }
  if (props.riskScore !== undefined) {
    url += `&riskScore=${props.riskScore}`;
  }
  if (props.unverified !== undefined) {
    url += `&unverified=${props.unverified}`;
  }
  if (props.deviceType !== undefined) {
    url += `&deviceType=${props.deviceType}`;
  }
    if (props.unverifiedByDate !== undefined) {
    url += `&unverifiedByDate=${props.unverifiedByDate}`;
  }
  if (props.dnsStatus !== undefined) {
    url += `&dnsStatus=${props.dnsStatus}`;
  }
  if (props.isDns) {
    const resp = await axios.post(
      url,
      { 
        params: props.params, 
        cpeId: props.cpeId ? props.cpeId : [], 
        devId: props.deviceId ? props.deviceId : [], 
        category: category ? category : categoryFilter 
      }
    );
    setCollection(resp.data.data);
    setAllData(resp.data.data);
    setTotal(resp.data.records);
    setLoading(false);
  } else {
    const resp = await axios.get(
      url,
      { params: props.params }
    );
    setCollection(resp.data.data);
    setAllData(resp.data.data);
    setTotal(resp.data.records);
    setLoading(false);
  }
};
  const tableRows = (rowData: any) => {
    const { key, index } = rowData;

    const columnData: any[] = tableHead.map((item: any, i: number) => {
      const keyValue = item.key || item;
      let value = key[keyValue];
      if (
        keyValue !== 'Summary' &&
        keyValue !== 'Age Profile' &&
        keyValue !== 'description' &&
        keyValue !== 'versions' &&
        keyValue !== 'hostname' &&
        keyValue !== 'host_name' &&
        keyValue !== 'profile' &&
        keyValue !== 'Name' &&
        isDate(value)
      ) {
        // let date = new Date(value);
        // let hour = String(date.getHours() - 1).padStart(2, '0');
        value = formatDate(value);
        // keyValue === 'timestamp' ? formatDate(value) : keyValue === 'Date Registered' ? formatDate(value) :
        // value = format(new Date(value), `dd-MMM-yyyy hh:mm:ss`).toUpperCase(); // Convert to desired format
      }
      if (keyValue === 'icons') {
        value = (
          <img style={{ width: '32px', height: '32px' }} src={`https://blackdice.ams3.digitaloceanspaces.com/appIcons/${value}.png`} />
        );
      }

      return (
        <td key={i} onClick={() => onSelectRow(rowData)} className='text-start'>
          {getTdValue(keyValue, value)}
        </td>
      );
    });

    if (props.onDelete)
      columnData.push(
        <td key={'acts'}>
          <div onClick={(e) => showDeleteConfirm(e, rowData)}>
            {' '}
            <img src="/icons/trash.png" className="trash" />
          </div>
        </td>
      );

    return (
      <tr
        key={index}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSelectRow(rowData);
        }}
      >
        {columnData}
      </tr>
    );
  };

  function getTdValue(key: any, value: any) {
    if (key.toLowerCase() === 'status') {
      return <div className={`status ${String(value ?? '').toLowerCase()}`}>{value}</div>;
    }
    // e.g. "9.8 CRITICAL" — tagged so design-system pages can render it as a severity chip
    const severity = key.toLowerCase().includes('severity') && String(value).match(/critical|high|medium|low/i)?.[0];
    if (severity) {
      return (
        <div>
          <span className={`severity sev-${severity.toLowerCase()}`}>{value}</span>
        </div>
      );
    } else {
      return (
        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip">{value === 'UNRESOLVED' ? t('tableWidget.invalidDomain') : value}</Tooltip>}>
          <div>{value === 'UNRESOLVED' ? t('tableWidget.invalidDomain') : value}</div>
        </OverlayTrigger>
      );
    }
  }

  function isDate(str: any) {
    let timestamp = Date.parse(str);
    if (isNaN(str) && timestamp !== 967777200000 && !isNaN(timestamp)) return new Date(timestamp);
    return false;
  }

  function formatDate(dateString: string) {
    const utcDate = new Date(dateString);
    // const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
    const hours = utcDate.getHours().toString().padStart(2, '0');
    const minutes = utcDate.getMinutes().toString().padStart(2, '0');
    const formattedSeconds = utcDate.getSeconds().toString().padStart(2, '0');
    const year = utcDate.getFullYear();
    const month = (utcDate.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed (0-11)
    const day = utcDate.getDate().toString().padStart(2, '0');
    // const formattedHours = hours.parStart()
    // const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
    // const [datePart, timePart] = utcDate.split('T');
    // const [year, month, day] = datePart.split('-');
    // const [hours, minutes, seconds] = timePart.split(':');
    // const formattedSeconds = seconds.split('.')[0];

    return `${day}-${month}-${year} ${hours}:${minutes}:${formattedSeconds}`;
    // const utcTimeString =  `${day}-${month}-${year} ${hours}:${minutes}:${formattedSeconds}`;
    // const utcDate = new Date(utcTimeString);
    // const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);

    // return localDate;
  }

  const tableData = () => {
    const tableData = collection?.slice(0, props.countPerPage)?.map((key: any, index: any) => tableRows({ key, index }));

    return tableData;
  };

  const headRow = () => {
    if (tableHead) {
      for (let i = 0; i < tableHead.length; i++) {
        if (tableHead[i] == 'haandleId' || tableHead[i] == 'HaandleId') {
          tableHead[i] = 'Haandle ID';
        }
      }
      return tableHead?.map((item: any, index: number) => <td key={index} className='text-start'>{StringUtils.toCamelCase(item.key || item)}</td>);
    }
  };

  function onSelectRow(rowData: any) {
    setSelectedRow(rowData);
    if (props.onRowSelect) {
      props.onRowSelect(rowData);
      return;
    }

    if (props.dnsRecords) {
      props.dnsRecords(rowData.key);
      return;
    }

    const data = Object.keys(rowData.key).map((key: any) => {
      return { key: StringUtils.toCamelCase(key), value: rowData.key[key] };
    });

    setModalDetails(data);
    setShowInfoModal(true);
  }

  async function onChangeDate(selectedIndex: number, dateStr: string) {
    setAllData([]);
    setCollection([]);
    setLoading(true);
    setSelectedDate(selectedIndex);
    setSelectedDateString(dateStr);

    const val = tags?.length ? tags.join('|') : searchRef.current?.value;
    await axios
      .get(`/${props.url}?page=1&size=${props.countPerPage}&q=${val || ''}&l=${dateStr}&type=${type}`, { params: props.params })
      .then((resp: any) => {
        setCurrentPage(1);
        setAllData(resp.data.data);
        setCollection(resp.data.data);
        setTotal(resp.data.records);
        setTableHead(resp.data.headers?.headers || resp.data.headers);
      });
    setLoading(false);
  }

  function showDeleteConfirm(e: any, rowData: any) {
    e.preventDefault();
    setSelectedRow(rowData);
    confirmDeleteRef.current.handleShow(); // onDelete runs only once the user confirms
  }

  async function deleteClicked() {
    props.onDelete(selectedRow);

    try {
      await axios.delete('/op/operatorcpe/' + selectedRow.key.account_id);
      showToastMessage(t('tableWidget.toast.deletionSuccessful'));
      reloadTable();
    } catch (e) {
      showToastMessage(String(e));
    }
  }

  function clearSearch() {
    debouncedResults.cancel();
    if (searchRef.current) searchRef.current.value = '';
    setHasQuery(false);
    searchRecords({ preventDefault() {}, target: { value: '' } });
    searchRef.current?.focus();
  }

  function reloadTable() {
    setCurrentPage(1);
    init();
  }

  async function onInputChange(e: any) {
    if (e.key === 'Enter') {
      setAllData([]);
      setCollection([]);
      setLoading(true);
      if (tags?.length && tags.find((item) => item === e.target.value)) return;
      const newTags = [...tags, e.target.value];
      setTags(newTags);
      interestInputRef.current.value = '';

      if (props.isDns) {
        const resp = await axios.post(
          `/${props.url}?page=1&size=${props.countPerPage}&q=${newTags.join(
            '|'
          )}&l=${selectedDateString}&startDate=${startDate}&endDate=${endDate}&countSearch=${countSearch}&type=${type}`,
          { params: props.params, cpeId: props.cpeId ? props.cpeId : [], devId: props.deviceId ? props.deviceId : [], category: categoryFilter }
        );
        setCurrentPage(1);
        const data = resp.data.data;
        setCollection(data);
        setTotal(resp.data.records);
      } else {
        const resp = await axios.get(
          `/${props.url}?page=1&size=${props.countPerPage}&q=${newTags.join(
            '|'
          )}&l=${selectedDateString}&startDate=${startDate}&endDate=${endDate}&cpeId=${cpeId}&devId=${deviceId}}`,
          {
            params: props.params,
          }
        );
        setCurrentPage(1);
        const data = resp.data.data;
        setCollection(data);
        setTotal(resp.data.records);
      }

      axios.patch(`op/${operatorId}/interests/update`, { interests: newTags.join(' ') });
      setLoading(false);
    }
  }

  async function onRemoveTag(tag: string) {
    setAllData([]);
    setCollection([]);
    setLoading(true);
    const newTags = tags.filter((t) => t !== tag);
    setTags(newTags);

    if (!newTags?.length) {
      updatePage(1, undefined);
      setTotal(allData ? allData.length : 0);
    } else {
      if (props.isDns) {
        const resp = await axios.post(
          `/${props.url}?page=1&size=${props.countPerPage}&q=${newTags.join(
            '|'
          )}&l=${selectedDateString}&startDate=${startDate}&endDate=${endDate}&countSearch=${countSearch}&type=${type}`,
          { params: props.params, cpeId: props.cpeId ? props.cpeId : [], devId: props.deviceId ? props.deviceId : [], category: categoryFilter }
        );
        setCurrentPage(1);
        const data = resp.data.data;
        setCollection(data);
        setTotal(resp.data.records);
      } else {
        const resp = await axios.get(
          `/${props.url}?page=1&size=${props.countPerPage}&q=${newTags.join(
            '|'
          )}&l=${selectedDateString}&startDate=${startDate}&endDate=${endDate}&cpeId=${cpeId}&devId=${deviceId}}`,
          {
            params: props.params,
          }
        );
        setCurrentPage(1);
        const data = resp.data.data;
        setCollection(data);
        setTotal(resp.data.records);
      }
    }
    axios.patch(`op/${operatorId}/interests/update`, { interests: newTags.join(' ') });
    setLoading(false);
  }

  async function onChangeCategoryFilter(event: any) {
    setLoading(true)
    setCategoriesFilter(event.target.value);
    if (props.isDns) {
      const resp = await axios.post(
        `/${props.url}?page=1&size=${props.countPerPage}&l=${selectedDateString}&startDate=${startDate}&endDate=${endDate}&countSearch=${countSearch}&type=${type}&q=${search}`,
        { params: props.params, cpeId: props.cpeId ? props.cpeId : [], devId: props.deviceId ? props.deviceId : [], category: event.target.value}
      );
      setCurrentPage(1);
      const data = resp.data.data;
      setCollection(data);
      setTotal(resp.data.records);
      setLoading(false)
    } else {
      const resp = await axios.get(
        `/${props.url}?page=1&size=${props.countPerPage}&q=${event.target.value}&l=${selectedDateString}&startDate=${startDate}&endDate=${endDate}`,
        {
          params: props.params,
        }
      );
      setCurrentPage(1);
      const data = resp.data.data;
      setCollection(data);
      setCategoriesFilter(event.target.value);
      setTotal(resp.data.records);
    }
  }

  const pageSize = Number(props.countPerPage) || 10;

  const nonSelected = 'flex md:w-[4rem] lg:w-[7rem] justify-center text-sm py-1 border-2';
  const selected = 'flex md:w-[4rem] lg:w-[7rem] options justify-center text-sm py-1 border-2 bg-green-500';

  return (
    <section style={{ height: props.height, borderRadius: 16 }} className={'retina-table ' + (loading ? 'loading' : '')}>
      <header className={props.searchPositionRight == true ? 'header-table flex' : 'header-table flex align-center justify-between p-2 m-2 pt-0 mt-0'}>
        <div className="flex flex-col align-center items-start">
          {props.icon && <img src={props.icon} style={{ width: '25px' }}></img>}
          <div className="">{props.title && <span>{props.title}</span>}</div>
          {props.subTitle && <a className="text-md text-gray-500">{props.subTitle}</a>}
        </div>

        {props.interests !== undefined && (
          <div className="input-group search">
            {tags.map((tag: string) => (
              <span key={tag} className="tag-box">
                {tag}
                <X onClick={() => onRemoveTag(tag)}></X>
              </span>
            ))}

            <input placeholder={t('tableWidget.interests.placeholder')} ref={interestInputRef} onKeyUp={onInputChange} />
          </div>
        )}

        {props.showAddAccount && (
          <Button onClick={() => registerAccountModal.current.handleShow()} className="register-btn">
            {t('tableWidget.actions.addAccount')}
          </Button>
        )}

        {props.showAddAccount && operatorId === '0' && (
          <Button
            onClick={() => {
              toggleAccount();
            }}
          >
            {t('tableWidget.actions.addRetinaAccount')}
          </Button>
        )}

        {props.interests === undefined && (
          <div className="search relative pt-3 items-center">
            {/* <img className="mr-3" width={30} height={30} onClick={reloadTable} src="/icons/refresh.svg" /> */}
            {props.hasSearch && (
              <input
                ref={searchRef}
                className="search-input"
                placeholder={t('tableWidget.search.placeholder')}
                aria-label={t('tableWidget.search.ariaLabel')}
                onChange={(e) => {
                  setHasQuery(!!e.target.value);
                  debouncedResults(e);
                }}
              />
            )}
            {props.hasSearch &&
              (hasQuery ? (
                <button type="button" className="search-clear absolute right-4" aria-label={t('tableWidget.search.clear')} onClick={clearSearch}>
                  <X size={16} />
                </button>
              ) : (
                <img className="absolute right-4" src="/icons/magnifying-glass.svg" alt=""></img>
              ))}
          </div>
        )}

        {props.hasCategoryFilter && (
          <div className="mt-4 mr-7">
            <Form.Select aria-label="" onChange={(event) => onChangeCategoryFilter(event)}>
              <option value={''}>{t('tableWidget.categoryFilter.none')}</option>
              {categorySearch?.map((category: string) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Form.Select>
          </div>
        )}
      </header>

      {props.defaultDate && !props.hideDates && (
        <div>
          <div className="col-lg-12 col-md-12">
            <div className="tw-range d-flex justify-content-center mb-3" role="group" aria-label={t('tableWidget.range.ariaLabel')} style={{ padding: '5px', marginTop: '10px' }}>
              {!props.hideTimes ? (
                <>
                  <button
                    className={selectedDate === 0 ? selected : nonSelected}
                    aria-pressed={selectedDate === 0}
                    onClick={() => onChangeDate(0, props.dates ? props.dates[0] : '1d')}
                    style={{ borderRadius: '16px 0px 0px 16px' }}
                  >
                   {t('tableWidget.range.today')}
                  </button>
                  <button
                    className={selectedDate === 1 ? selected : nonSelected}
                    aria-pressed={selectedDate === 1}
                    onClick={() => onChangeDate(1, props.dates ? props.dates[1] : '7')}
                  >
                    {t('tableWidget.range.pastDays', { count: Number(props.dates ? props.dates[1].replace('d', '') : '7') })}
                  </button>
                  <button
                    className={selectedDate === 2 ? selected : nonSelected}
                    aria-pressed={selectedDate === 2}
                    style={{ borderRadius: '0px 16px 16px 0px' }}
                    onClick={() => onChangeDate(2, props.dates ? props.dates[2] : '30')}
                  >
                    {t('tableWidget.range.pastDays', { count: Number(props.dates ? props.dates[2].replace('d', '') : '30') })}
                  </button>
                  {/* {!props.isCve && (
                    <button
                      className={selectedDate === 3 ? selected : nonSelected}
                      style={{ borderRadius: '0px 16px 16px 0px' }}
                      onClick={() => onChangeDate(3, '60')}
                    >
                      Past 60 days
                    </button>
                  )} */}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <Table bordered hover responsive>
          <thead>
            <tr>{headRow()}</tr>
          </thead>
          <tbody className="trhover">{tableData()}</tbody>
        </Table>

        {!loading && !collection?.length && <div className="no-data">{t('tableWidget.noData')}</div>}
        {loading && (
          <div className="table-loading-overlay">
            <LoadingSpinner />
          </div>
        )}
      </div>

      {!!total && (
        <footer className="table-footer">
          <span className="table-range">
            {t('tableWidget.footer.showing', { from: (currentPage - 1) * pageSize + 1, to: Math.min(currentPage * pageSize, total), total: total.toLocaleString() })}
          </span>
          {total > pageSize && (
            <Pagination
              className="pagination"
              pageSize={pageSize}
              onChange={(e) => updatePage(e, tags.join('|'))}
              current={currentPage}
              nextIcon={<CustomNextIcon />}
              prevIcon={<CustomPreviousIcon />}
              total={total}
            />
          )}
        </footer>
      )}
      <div>
        {' '}
        <Modal show={showAccount} onHide={toggleAccount} onClose={reloadTable}>
          <Modal.Header closeButton></Modal.Header>
          <Modal.Body>
            <form onSubmit={addAccount}>
              <h2 className="mb-3">{t('tableWidget.register.title')}</h2>
              <div>
                <label>{t('tableWidget.register.email')}</label>
                <input className="form-control" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>

              <div className="mt-3">
                <label>{t('tableWidget.register.password')}</label>
                <input
                  className="form-control"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="mt-3">
                <label>{t('tableWidget.register.confirmPassword')}</label>
                <input
                  className="form-control"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              <Button className="btn btn-primary mt-3" type="submit">
                {t('tableWidget.register.submit')}
              </Button>
            </form>
          </Modal.Body>
        </Modal>
      </div>
      <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)} centered dialogClassName="global-modal table-modal">
        {/*   <Modal.Header>
          <Modal.Title>Details</Modal.Title>
        </Modal.Header> */}
        <Modal.Body>
          <BreakdownList title={t('tableWidget.details.title')} subtitle="Test" icon="/icons/cube.svg" data={modalDetails} />
        </Modal.Body>
      </Modal>
      {/* <ToastMessage ref={toastRef} /> */}

      <RegisterAccount onClose={reloadTable} ref={registerAccountModal} title={t('tableWidget.registerAccount.title')} />
      <ConfirmModal
        ref={confirmDeleteRef}
        onConfirm={deleteClicked}
        title={t('tableWidget.delete.title')}
        confirmBtn={t('tableWidget.delete.confirm')}
        description={t('tableWidget.delete.description', { id: selectedRow?.key?.account_id ?? selectedRow?.key?.ID })}
      />
    </section>
  );
};

export default function CustomNextIcon() {
  return (
    <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.943787" y="0.5" width="31" height="31" rx="7.5" stroke="#BBCEDB" />
      <path
        d="M14.4438 20L18.1183 16.7071C18.5523 16.3182 18.5523 15.6818 18.1183 15.2929L14.4438 12"
        stroke="#BBCEDB"
        strokeWidth="2"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const CustomPreviousIcon = () => (
  <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="31.9438" y="31.5" width="31" height="31" rx="7.5" transform="rotate(-180 31.9438 31.5)" stroke="#BBCEDB" />
    <path
      d="M18.4438 12L14.7693 15.2929C14.3353 15.6818 14.3353 16.3182 14.7693 16.7071L18.4438 20"
      stroke="#BBCEDB"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
